// ── 출퇴근 모바일 요약 (직원별 아코디언 + 바 시각화) — Phase 1-2 B ────────────
// 연차 SummarySection.jsx 패턴을 출퇴근에 맞게 변형:
//   · 한 번에 한 직원만 펼침 (expandedId)
//   · 직원별 stat 클릭 → 다중 OR 필터 (filtersPerUser, 0개수는 자동 숨김)
//   · 직원별 페이지네이션 (pagePerUser, 10건 단위)
//   · 펼침 토글 시 이전 직원의 필터/페이지 리셋 (혼란 방지)
import React, { useEffect, useState } from 'react';
import {
  ATTENDANCE_FILTER_DEFS, matchesAttendanceFilter,
  getRowTotalMinutes, getRowBreakMinutes, formatMinutesKorean,
} from './utils';
import { btn, mobileEmptyStyle } from './styles';

const PAGE_SIZE = 10; // 직원별 펼침 내부 페이지 크기

// hex 색상 → "R,G,B" 문자열 (rgba(...,0.15)/(...,0.3) 합성용)
function hexToRgb(hex) {
  if (!hex || !hex.startsWith('#')) return '100,116,139';
  return `${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)}`;
}
const tintBg = (hex) => `rgba(${hexToRgb(hex)},0.15)`;
const tintBd = (hex) => `rgba(${hexToRgb(hex)},0.3)`;

/**
 * 모바일용 직원별 요약 카드 리스트 (B)
 * Props:
 *   groupedByUser — useAttendanceData.groupedByUser ({ [user_id]: { user, counts, workDays, offDays, rows } })
 *   onEdit(row)   — 행 클릭 시 수정 모달 콜백
 *   focusUserId   — (선택) 처음부터 펼쳐 둘 직원. 달력 탭에서 이름을 눌러 왔을 때(2026-09-29) — 그 카드로 화면도 내린다.
 */
export function AttendanceMobileSummary({ groupedByUser, onEdit, focusUserId = null }) {
  const [expandedId, setExpandedId] = useState(() => (Number(focusUserId) > 0 ? Number(focusUserId) : null));
  const hasGroups = Object.keys(groupedByUser || {}).length > 0;
  useEffect(() => {
    if (!(Number(focusUserId) > 0) || !hasGroups) return;
    const el = document.getElementById(`att-m-${Number(focusUserId)}`);
    if (el) el.scrollIntoView({ block: 'center' });
  }, [focusUserId, hasGroups]);
  const [filtersPerUser, setFiltersPerUser] = useState({});
  const [pagePerUser, setPagePerUser] = useState({});

  // 펼침 토글 — 같은 직원 클릭 시 닫음, 다른 직원 클릭 시 이전 필터/페이지 리셋
  const toggleExpand = (uid) => {
    if (expandedId === uid) { setExpandedId(null); return; }
    setExpandedId(uid);
    setFiltersPerUser({});
    setPagePerUser({});
  };

  // 직원 내부 stat 필터 토글 (다중 OR) — 토글 시 페이지 1로 리셋
  const toggleFilter = (uid, key, e) => {
    e.stopPropagation();
    setFiltersPerUser((prev) => {
      const cur = prev[uid] || [];
      return { ...prev, [uid]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });
    setPagePerUser((prev) => ({ ...prev, [uid]: 1 }));
  };

  // 직원 목록은 이름 가나다순 (안정적 표시)
  const groups = Object.values(groupedByUser).sort((a, b) =>
    String(a.user.name || '').localeCompare(String(b.user.name || ''), 'ko'),
  );

  if (groups.length === 0) {
    return <div style={mobileEmptyStyle}>데이터 없음</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {groups.map((g) => {
        const uid = g.user.id;
        const expanded = expandedId === uid;
        const userFilters = filtersPerUser[uid] || [];
        // 펼침 내부 행 — 사용자 필터 적용 (없으면 전체)
        const visibleRows = userFilters.length > 0
          ? g.rows.filter((row) => userFilters.some((k) => matchesAttendanceFilter(row, k)))
          : g.rows;
        const page = pagePerUser[uid] || 1;
        const totalPages = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
        const pagedRows = visibleRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

        const totalDays = g.workDays + g.offDays;
        // 0개수 stat은 숨김 (배지 노이즈 제거)
        const stats = ATTENDANCE_FILTER_DEFS.filter((f) => Number(g.counts[f.key] || 0) > 0);

        return (
          <article key={`m-${uid}`} id={`att-m-${uid}`} style={cardStyle}>
            {/* 1행: 이름 + 회사 + 부서 + 일수 요약 (탭 → 아코디언 토글) */}
            <div onClick={() => toggleExpand(uid)} style={headerRowStyle}>
              <strong style={{ fontSize: 14, fontWeight: 700 }}>{g.user.name}</strong>
              {g.user.company_name && (
                <span style={companyBadgeStyle}>{g.user.company_name}</span>
              )}
              {g.user.department && (
                <span style={deptTextStyle}>{g.user.department}</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                근무 {g.workDays} · 휴무 {g.offDays}
              </span>
            </div>

            {/* 2행: 바 시각화 (totalDays 기준 비율, 0개수 segment는 자동 미렌더) */}
            {totalDays > 0 && (
              <div style={barWrapStyle}>
                {ATTENDANCE_FILTER_DEFS.map((f) => {
                  const cnt = Number(g.counts[f.key] || 0);
                  if (cnt === 0) return null;
                  const w = (cnt / totalDays) * 100;
                  return (
                    <div
                      key={f.key}
                      title={`${f.label} ${cnt}일`}
                      style={{ width: `${w}%`, background: f.color, height: '100%' }}
                    />
                  );
                })}
              </div>
            )}

            {/* 3행: 숫자 필터 (0 숨김, 다중 토글) + 펼침 인디케이터 */}
            <div style={statRowStyle}>
              {stats.map((f, i) => {
                const active = userFilters.includes(f.key);
                const cnt = Number(g.counts[f.key] || 0);
                return (
                  <React.Fragment key={f.key}>
                    {i > 0 && <span style={{ color: 'var(--border)' }}>·</span>}
                    <span
                      onClick={(e) => toggleFilter(uid, f.key, e)}
                      style={{
                        cursor: 'pointer', padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                        ...(active
                          ? { background: tintBg(f.color), color: f.color, border: `1px solid ${tintBd(f.color)}` }
                          : { color: 'var(--text-sub)', border: '1px solid transparent' }),
                      }}
                    >
                      {f.label} {cnt}
                    </span>
                  </React.Fragment>
                );
              })}
              <span
                onClick={() => toggleExpand(uid)}
                style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                {expanded ? '▲' : '▼'}
              </span>
            </div>

            {/* 펼침: 행 리스트 + 페이지네이션 */}
            {expanded && (
              <div style={expandBoxStyle}>
                {pagedRows.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>
                    데이터 없음
                  </div>
                )}
                {pagedRows.map((row) => {
                  const isLate       = Number(row.late_minutes) > 0;
                  const isEarly      = Number(row.early_leave_minutes) > 0;
                  const isMissingOut = Boolean(row.check_in) && !row.check_out;
                  const isDayoff     = Boolean(row.is_dayoff);
                  const total        = getRowTotalMinutes(row);
                  const breakMin     = getRowBreakMinutes(row);
                  const workTime     = total === null ? '-' : formatMinutesKorean(Math.max(0, total - (breakMin || 0)));
                  return (
                    <div
                      key={row.id}
                      onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                      style={rowCardStyle}
                    >
                      {/* 한 줄: 날짜 · 출근 · 퇴근 · 색상 점 (지각/조퇴/미체크/휴무/휴무근무) — 폰트 12px */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <span style={{ width: 42, color: 'var(--text-sub)', flexShrink: 0 }}>
                          {String(row.date_ymd || '').slice(5) || '-'}
                        </span>
                        <span style={{ color: 'var(--text)', whiteSpace: 'nowrap' }}>
                          {row.check_in_hhmm || '-'}<span style={{ color: 'var(--text-muted)' }}>·</span>{row.check_out_hhmm || (row.check_in ? <span style={{ color: '#dc2626' }}>미체크</span> : '-')}
                        </span>
                        {/* 상태 점 (한 줄 유지 위해 작은 동그라미로 압축) */}
                        {isLate       && <span title={`지각 ${Number(row.late_minutes)}분`} style={dotStyle('#d97706')} />}
                        {isEarly      && <span title={`조퇴 ${Number(row.early_leave_minutes)}분`} style={dotStyle('#c2410c')} />}
                        {isMissingOut && <span title="퇴근미체크" style={dotStyle('#dc2626')} />}
                        {isDayoff     && <span title="휴무" style={dotStyle('#64748b')} />}
                        {row.is_offday_work && <span title="휴무근무" style={dotStyle('#15803d')} />}
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                          {workTime}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {totalPages > 1 && (
                  <div style={paginationRowStyle}>
                    <button
                      style={pageBtnStyle}
                      disabled={page <= 1}
                      onClick={(e) => { e.stopPropagation(); setPagePerUser((p) => ({ ...p, [uid]: Math.max(1, page - 1) })); }}
                    >이전</button>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{page} / {totalPages}</span>
                    <button
                      style={pageBtnStyle}
                      disabled={page >= totalPages}
                      onClick={(e) => { e.stopPropagation(); setPagePerUser((p) => ({ ...p, [uid]: Math.min(totalPages, page + 1) })); }}
                    >다음</button>
                  </div>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ── 인라인 스타일 (모바일 전용 / 다크 모드 대응 — CSS 변수 사용) ────────────
const cardStyle = {
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 10,
  background: 'var(--bg-card)',
  display: 'grid',
  gap: 4,
};
const headerRowStyle = {
  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
};
const companyBadgeStyle = {
  fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
  background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a',
};
const deptTextStyle = { fontSize: 11, color: 'var(--text-muted)' };
const barWrapStyle = {
  display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden',
  background: 'var(--bg-subtle)', marginTop: 2,
};
const statRowStyle = {
  display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, alignItems: 'center', marginTop: 2,
};
const expandBoxStyle = {
  borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 4,
  display: 'grid', gap: 4,
};
const rowCardStyle = {
  padding: '6px 8px', borderRadius: 6, background: 'var(--bg-subtle)', cursor: 'pointer',
};
// 한 줄 행 안 상태 점 (지각/조퇴/미체크/휴무 색상 표시)
const dotStyle = (color) => ({
  width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block',
});
const paginationRowStyle = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 4, fontSize: 12,
};
const pageBtnStyle = {
  ...btn, padding: '2px 10px', fontSize: 12,
};
